import reactUpdate from 'immutability-helper';

import {assert} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {TagSchema} from 'constants/RegistryConstants';
import {
  CreateMixtureGroupExtras,
  MixtureGroupMetadata,
} from 'notebook/integrations/registry/MixtureGroupConstants';
import {MixtureIngredientsStructuredTable} from 'notebook/integrations/registry/MixtureIngredientsStructuredTableAdapter';
import {MixtureMetadataStructuredTable} from 'notebook/integrations/registry/MixtureMetadataStructuredTableAdapter';
import {
  reduceGroupHandleParentMixtureChange,
  reduceGroupHandleUpdatedMixturePrepAmount,
} from 'notebook/integrations/registry/MixtureNoteLinkedGroupReducers';
import {getMixtureMetadataStructuredTableAdapter} from 'notebook/integrations/registry/MixtureTableAdapters';
import {makeDefaultItem} from 'notebook/notes/CreateOps';
import addMixtureGroupToWorklistActionFunction from 'notebook/notes/groups/action-functions/addMixtureGroupToWorklistActionFunction';
import createRequestForMixtureGroupActionFunction from 'notebook/notes/groups/action-functions/createRequestForMixtureGroupActionFunction';
import {
  downloadIngredientsCSVActionFunction,
  downloadMixtureCSVActionFunction,
} from 'notebook/notes/groups/action-functions/downloadMixtureGroupCSVActionFunctions';
import insertParentMixtureInstructionsActionFunction from 'notebook/notes/groups/action-functions/insertParentMixtureInstructionsActionFunction';
import printMixtureGroupBarcodeActionFunction from 'notebook/notes/groups/action-functions/printMixtureGroupBarcodeActionFunction';
import refreshMixtureNoteLinkedGroupActionFunction from 'notebook/notes/groups/action-functions/refreshMixtureNoteLinkedGroupActionFunction';
import submitMixtureGroupActionFunction from 'notebook/notes/groups/action-functions/submitMixtureGroupActionFunction';
import GenericNoteLinkedGroupAdapter from 'notebook/notes/groups/GenericNoteLinkedGroupAdapter';
import {
  CreateNoteLinkedGroupProps,
  GroupActionFunctions,
  NoteLinkedGroup,
  NoteLinkedGroupKind,
} from 'notebook/notes/groups/NoteLinkedGroupConstants';
import {
  findMatchingNotePartInGroup,
  reduceGroupUpdateNotePart,
} from 'notebook/notes/groups/util/ContainedNotePartUtil';
import groupKindToIcon from 'notebook/notes/groups/util/groupKindToIcon';
import {SamplesSource} from 'notebook/sample-propagation/sample-collection/SamplesSourceCollector';
import {SampleFilterType} from 'notebook/sample-propagation/SampleFilter';
import {hydrateBioentityFromSampleId} from 'notebook/sample-propagation/util/hydrateSampleId';
import {makeBioentitySampleDetails} from 'notebook/sample-propagation/util/makeSampleDetails';
import {EXISTING_BIOENTITY_COLUMN_NAME} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {StructuredTableKind} from 'notebook/structured-tables/StructuredTableConstants';
import {STRUCTURED_TABLE_PLUGIN} from 'notebook/structured-tables/StructuredTablePlugin';
import {EMPTY_PLACEHOLDER_INSERTION_CONTEXT} from 'notebook/structured-tables/util/StructuredTableInsertionContext';
import {
  isMixtureIngredientsStructuredTable,
  isMixtureMetadataStructuredTable,
  isNotePartWithMixtureMetadataStructuredTable,
} from 'notebook/TypePredicates';
import {isNotePartWithItem, NotePart, NotePartWithItem} from 'notebook/util/NoteUtilConstants';
import {Schema} from 'schemas/SchemaConstants';
import {MaybePromise} from 'util/Promises';

export const GroupKind = 'MIXTURE_GROUP';
export type GroupKind = typeof GroupKind;

export type MixtureGroup = NoteLinkedGroup<MixtureGroupMetadata>;

export class MixtureNoteLinkedGroupAdapter extends GenericNoteLinkedGroupAdapter<
  MixtureGroupMetadata,
  CreateMixtureGroupExtras
> {
  getDisplayName(mixtureGroup: MixtureGroup): string {
    return 'Create mixture prep';
  }

  override getActionFunctions(): GroupActionFunctions<MixtureGroupMetadata, CreateMixtureGroupExtras> {
    const inherited = super.getActionFunctions();
    return {
      primary: submitMixtureGroupActionFunction,
      secondary: [
        insertParentMixtureInstructionsActionFunction,
        refreshMixtureNoteLinkedGroupActionFunction,
        createRequestForMixtureGroupActionFunction,
        addMixtureGroupToWorklistActionFunction,
        printMixtureGroupBarcodeActionFunction,
      ],
      overflow: [
        // TODO: Figure out if this use of ! is safe.
        ...inherited.overflow!,
        downloadMixtureCSVActionFunction,
        downloadIngredientsCSVActionFunction,
      ],
      footer: inherited.footer ?? [],
    };
  }

  createNoteParts(
    createProps: CreateNoteLinkedGroupProps<MixtureGroupMetadata, CreateMixtureGroupExtras>
  ): Array<NotePart> {
    const {name, kind, schemas, entryId, selectorsById, isConfig, extras} = createProps;
    assert(kind === NoteLinkedGroupKind.MIXTURE_GROUP, 'This adapter only works for Mixture Groups');
    assert(schemas.length === 1, 'Mixture group only allows one schema');
    const [schema] = schemas;

    const metadataTable = STRUCTURED_TABLE_PLUGIN.get(StructuredTableKind.MIXTURE_METADATA).createTable({
      name: extras.getNextUniqueName(`${name} mixture`, {allowNoSuffix: true}),
      schemas: [{...schema, columnGroup: null}],
      entryId,
      metadata: extras.mixtureMetadataTableMetadata,
      selectorsById,
      isConfig,
      structuredTableInsertionContext: EMPTY_PLACEHOLDER_INSERTION_CONTEXT,
    });
    const ingredientsTable = STRUCTURED_TABLE_PLUGIN.get(StructuredTableKind.MIXTURE_INGREDIENTS).createTable(
      {
        name: extras.getNextUniqueName(`${name} ingredients`, {allowNoSuffix: true}),
        schemas: [{...schema, columnGroup: null}],
        entryId,
        metadata: {
          searchAllSchemas: true,
        },
        selectorsById,
        isConfig,
        structuredTableInsertionContext: EMPTY_PLACEHOLDER_INSERTION_CONTEXT,
      }
    );

    // @ts-expect-error(2322): This is passing in `shouldGenerateCid: false` but we are expecting it to have a cid
    return [metadataTable, ingredientsTable].map((structuredTable) =>
      reactUpdate(makeDefaultItem(ModelTypeConstants.NOTE_LINKED_OBJECT, null, {shouldGenerateCid: false}), {
        // @ts-expect-error(2322): The NotePart StructuredTable mapping uses unknown instead of a type parameter
        item: {$set: structuredTable},
      })
    );
  }

  override reduceGroup(
    noteLinkedGroup: MixtureGroup,
    updatedNotePart:
      | NotePartWithItem<MixtureMetadataStructuredTable>
      | NotePartWithItem<MixtureIngredientsStructuredTable>
  ): MaybePromise<MixtureGroup> {
    // We always want to at least update the note part that changed in the group.
    // NOTE: It's important to do this update first and then pass the result along to the other reducers,
    // because reduceGroupHandleParentMixtureChange may make further changes to `updatedNotePart` and
    // incorporate that version into the group, so we need to avoid overriding that.
    const withBasicUpdate = reduceGroupUpdateNotePart<MixtureGroupMetadata>(noteLinkedGroup, updatedNotePart);

    if (isNotePartWithMixtureMetadataStructuredTable(updatedNotePart)) {
      // The only changes that we need to propagate updates from would be in the note part corresponding to
      // the mixture metadata table
      const currentNotePart = findMatchingNotePartInGroup(
        noteLinkedGroup,
        updatedNotePart
      ) as NotePartWithItem<MixtureMetadataStructuredTable>;
      assert(
        currentNotePart != null,
        `Expected a matching note part for updatedNotePart: type=${updatedNotePart.type}, cid=${updatedNotePart.cid}`
      );

      const updatedForAmount = reduceGroupHandleUpdatedMixturePrepAmount(
        withBasicUpdate,
        currentNotePart,
        updatedNotePart
      );
      return reduceGroupHandleParentMixtureChange(updatedForAmount, currentNotePart, updatedNotePart);
    } else {
      return withBasicUpdate;
    }
  }

  getTagSchema(group: MixtureGroup): TagSchema | null {
    return getMixtureMetadataStructuredTableAdapter().getTagSchema(
      this.getMixtureMetadataStructuredTable(group)
    );
  }

  getMixtureMetadataNotePart(mixtureGroup: MixtureGroup): NotePartWithItem<MixtureMetadataStructuredTable> {
    const metadataTableNotePart = mixtureGroup.containedNoteParts[0];
    assert(
      isNotePartWithItem(metadataTableNotePart) &&
        isMixtureMetadataStructuredTable(metadataTableNotePart.item),
      'First note part in group must be mixture metadata table'
    );
    return metadataTableNotePart as NotePartWithItem<MixtureMetadataStructuredTable>;
  }

  getMixtureMetadataStructuredTable(mixtureGroup: MixtureGroup): MixtureMetadataStructuredTable {
    return this.getMixtureMetadataNotePart(mixtureGroup).item;
  }

  getMixtureIngredientsNotePart(
    mixtureGroup: MixtureGroup
  ): NotePartWithItem<MixtureIngredientsStructuredTable> {
    const ingredientsTableNotePart = mixtureGroup.containedNoteParts[1];
    assert(
      isNotePartWithItem(ingredientsTableNotePart) &&
        isMixtureIngredientsStructuredTable(ingredientsTableNotePart.item),
      'Second note part in group must be ingredients table'
    );
    return ingredientsTableNotePart as NotePartWithItem<MixtureIngredientsStructuredTable>;
  }

  getMixtureIngredientsStructuredTable(mixtureGroup: MixtureGroup): MixtureIngredientsStructuredTable {
    return this.getMixtureIngredientsNotePart(mixtureGroup).item;
  }

  getMixtureId(group: MixtureGroup): string | null {
    const metadataTable = this.getMixtureMetadataStructuredTable(group);
    return getMixtureMetadataStructuredTableAdapter().getMixtureId(metadataTable);
  }

  // This exposes a single source that represents the group as a whole. Sub-tables are exposed for propagation separately.
  override getGroupSampleSources(group: MixtureGroup): Array<SamplesSource> {
    const schemaId = this.getTagSchema(group)?.id;
    const sampleId = this.getMixtureId(group);
    const bioentityInfo = sampleId ? hydrateBioentityFromSampleId(sampleId) : null;
    const sample = bioentityInfo ? makeBioentitySampleDetails(bioentityInfo) : null;

    return schemaId && sample
      ? [
          {
            name: group.name,
            icon: groupKindToIcon(NoteLinkedGroupKind.MIXTURE_GROUP),
            samplesByColumn: [
              {
                // This sample always comes from the EXISTING_BIOENTITY column of the mixture metadata table
                displayName: EXISTING_BIOENTITY_COLUMN_NAME,
                sampleFilter: {
                  type: SampleFilterType.BIOENTITY,
                  schemaId,
                },
                samples: [sample],
              },
            ],
            table: null,
          },
        ]
      : [];
  }

  getGroupSchemas(group: MixtureGroup): Array<Schema> {
    const mixtureSchema = this.getTagSchema(group);
    return mixtureSchema ? [mixtureSchema] : [];
  }
}
